import { gql } from '@apollo/client';
import { Alert, Button, Checkbox, Form, Select } from 'antd';
import FormLabel from 'components/FormLabel';
import { useMutation } from 'hooks/apollo';
import React from 'react';

import LicenseTag from 'components/LicenseTag';
import useDataSource from 'hooks/useDataSource';
import { PrivacySetting } from 'shared/services/prisma/types';
import PrivacySettingLabel from 'components/PrivacySettingLabel';
import Page from 'components/Page';
import messages from '../messages';

const FIELDS = `
  id
  matomoId
  trackVisitors
  useCookies
  matomoSettings {
    id
    disableVisitorLog
    disableVisitorProfile
  }
`;

const extraFields = `
  website {
    ${FIELDS}
  }
`;

const UPDATE_WEBSITE = gql`
  mutation($id: ID!, $input: WebsiteInput!) {
    updateWebsite(id: $id, input: $input) {
      ${FIELDS}
    }
  }
`;

const privacySettingOptions = Object.values(PrivacySetting);

const cookieOptions = [
  { value: PrivacySetting.Always, label: 'When tracking' },
  { value: PrivacySetting.WithConsent, label: 'With cookie consent' },
  { value: PrivacySetting.Never, label: 'Cookieless tracking' },
];

const PrivacySettings = () => {
  const { dataSource, getDataSourceLicenseSetting, loading } = useDataSource({ extraFields });
  const [updateWebsite, { loading: updating }] = useMutation(UPDATE_WEBSITE);

  const { website } = dataSource || {};
  const websiteId = website?.id;

  const canEditDisableCookies = getDataSourceLicenseSetting('disableCookies');
  const onSubmit = (values) => updateWebsite({ variables: { id: websiteId, input: values } });

  return (
    <Page loading={loading}>
      <Alert
        message={(
          <>
            Read more about our
            {' '}
            <a href="https://academy.extellio.com/knowledge-base/get-started/what-cookies-do-extellio-use" target="_blank">cookies</a>
            {' '}
            and how to
            {' '}
            <a href="https://academy.extellio.com/knowledge-base/get-started/how-to-turn-on-cookieless-analytics">handle consent</a>
            .
          </>
          )}
        type="info"
        showIcon
      />
      <Page.Content title="Privacy & cookie settings">
        <Form onFinish={onSubmit} initialValues={website} layout="vertical">
          <Form.Item name="trackVisitors" label={<FormLabel label={messages.trackVisitorsLabel} description={messages.trackVisitorsDescription} />}>
            <Select options={privacySettingOptions.map(o => ({ value: o, label: <PrivacySettingLabel value={o} /> }))} />
          </Form.Item>
          <Form.Item name="useCookies" label={<FormLabel label={messages.useCookiesLabel} description={messages.useCookiesDescription} extra={!canEditDisableCookies && <LicenseTag setting="disableCookies" showTooltip popoverProps={{ placement: 'left' }} />} />}>
            <Select options={cookieOptions.map(o => ({ value: o.value, label: o.label, disabled: o.value === PrivacySetting.Never && !canEditDisableCookies }))} />
          </Form.Item>
          <Form.Item
            name={['matomoSettings', 'disableVisitorLog']}
            valuePropName="checked"
          >
            <Checkbox>
              <FormLabel label={messages.disableVisitorLogLabel} description={messages.disableVisitorLogDescription} />
            </Checkbox>
          </Form.Item>
          <Form.Item
            name={['matomoSettings', 'disableVisitorProfile']}
            valuePropName="checked"
          >
            <Checkbox>
              <FormLabel label={messages.disableVisitorProfileLabel} description={messages.disableVisitorProfileDescription} />
            </Checkbox>
          </Form.Item>
          <Page.Footer sticky>
            <Button type="primary" htmlType="submit" loading={updating}>Save</Button>
          </Page.Footer>
        </Form>
      </Page.Content>
    </Page>
  );
};

export default PrivacySettings;
